<template>
  <div>
    <!-- Encabezado con título y botones de acción -->
    <Head :title="`Compra #${shopping.id} ${shopping.status === 'cancelled' ? '| COMPRA ANULADA' : ''}`" />

    <div class="flex justify-between items-center mb-6">
      <div class="flex items-center">
        <button 
          class="mr-4 p-3 text-white bg-teal-800 hover:bg-teal-900 rounded-lg transition-colors" 
          @click="back()"
          title="Volver atrás"
        >
          <i class="fa fa-arrow-left"></i>
        </button>
        
        <h1 class="text-3xl font-bold">
          Compra #{{ shopping.id }}
          <span v-if="shopping.status === 'cancelled'" class="text-red-500 ml-2">
            COMPRA ANULADA
          </span>
          <span v-if="shopping.status === 'paid'" class="text-blue-500 ml-2">
            COMPRA PAGADA
          </span>
        </h1>
      </div>

      <!-- Botones de impresión -->
      <div class="flex gap-2">
        <a 
          target="_blank" 
          :href="`/shoppings/${shopping.id}/report`"
          class="btn-blue p-3 rounded hover:opacity-90 transition-opacity"
        >
          <i class="fa fa-print text-white mr-2" />
          Imprimir Compra
        </a>
      </div>
    </div>

    <!-- Información del proveedor y detalles de la compra -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6 p-6 bg-white rounded-lg shadow">
      <div class="space-y-3">
        <div class="grid grid-cols-2 gap-2">
          <div class="font-semibold">Proveedor:</div>
          <div>{{ shopping.provider?.company_name ?? shopping.provider?.name }}</div>

          <div class="font-semibold">RIF/Cédula:</div>
          <div>{{ shopping.provider?.tax_id }}</div>

          <div class="font-semibold">Email:</div>
          <div>{{ shopping.provider?.email }}</div>

          <div class="font-semibold">Teléfono:</div>
          <div>{{ shopping.provider?.phone }}</div>
        </div>

        <div class="border-t pt-3 mt-3">
          <div class="grid grid-cols-2 gap-2">
            <div class="font-semibold">Factura:</div>
            <div>#{{ shopping.invoice_number }}</div>

            <div class="font-semibold">Fecha:</div>
            <div>{{ formatDate(shopping.date) }}</div>

            <div class="font-semibold">Vencimiento:</div>
            <div>{{ formatDate(shopping.due_date) }}</div>

            <div class="font-semibold">Total:</div>
            <div>${{ shopping.total }}</div>

            <div class="font-semibold">Adeudo:</div>
            <div>${{ shopping.total - shopping.total_paid }}</div>

            <div class="font-semibold">Estado:</div>
            <div>
              <span 
                class="px-3 py-1 rounded-full text-sm"
                :class="{
                  'bg-green-100 text-green-800': shopping.status === 'completed',
                  'bg-yellow-100 text-yellow-800': shopping.status === 'pending',
                  'bg-red-100 text-red-800': shopping.status === 'cancelled',
                  'bg-blue-100 text-blue-800': shopping.status === 'paid'
                }"
              >
                {{ shopping.status === 'pending' ? 'Pendiente' : shopping.status === 'cancelled' ? 'Anulada' : shopping.status === 'paid' ? 'Pagada' : shopping.status }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sección de productos -->
    <div class="p-6 bg-white rounded-lg shadow">
      <h2 class="text-xl font-bold mb-6">Productos</h2>

      <div class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="bg-gray-50 text-center">
              <th class="p-3">Producto</th>
              <th class="p-3">Código</th>
              <th class="p-3">Cantidad</th>
              <th class="p-3">Precio</th>
              <th class="p-3">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="product in shopping.products"
              :key="product.id"
              class="border-t hover:bg-gray-50 transition-colors text-center"
            >
              <td class="p-3">{{ product.product.name }}</td>
              <td class="p-3">{{ product.product.code }}</td>
              <td class="p-3">{{ product.quantity }}</td>
              <td class="p-3">${{ product.price }}</td>
              <td class="p-3">${{ (product.price * product.quantity).toFixed(2) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="bg-gray-50 font-semibold">
              <td colspan="4" class="p-3 text-right">Total:</td>
              <td class="p-3">${{ shopping.total }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Tabla de pagos -->
    <div class="mt-6 bg-white rounded-lg shadow">
      <div class="p-6">
        <h2 class="text-xl font-bold mb-6">Pagos Realizados</h2>
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="bg-gray-50 text-center">
                <th class="p-3 font-semibold">Fecha</th>
                <th class="p-3 font-semibold">Método</th>
                <th class="p-3 font-semibold">Monto USD</th>
                <th class="p-3 font-semibold">Monto Bs</th>
                <th class="p-3 font-semibold">Tasa</th>
                <th class="p-3 font-semibold">Referencia</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="payment in shopping.payments" :key="payment.id" class="border-t hover:bg-gray-50 transition-colors text-center">
                <td class="p-3">{{ formatDate(payment.created_at) }}</td>
                <td class="p-3">{{ getPaymentMethodName(payment.payment_method_id) }}</td>
                <td class="p-3">{{ formatCurrency(payment.amount_usd) }}</td>
                <td class="p-3">{{ payment.amount_ves ? formatCurrency(payment.amount_ves, 'VES') : '-' }}</td>
                <td class="p-3">{{ payment.exchange_rate ? payment.exchange_rate : '-' }}</td>
                <td class="p-3">{{ payment.reference || '-' }}</td>
              </tr>
              <tr v-if="!shopping.payments.length" class="text-center text-gray-500">
                <td colspan="6" class="p-4">No hay pagos registrados</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Sección para agregar pagos -->
    <div v-if="canManagePayments" class="mt-6 bg-white rounded-lg shadow">
      <div class="p-6">
        <h2 class="text-xl font-bold mb-6">Registrar Nuevo Pago</h2>

        <div class="bg-blue-50 p-4 rounded mb-6">
          <p class="flex items-center">
            <i class="fa fa-info-circle text-blue-500 mr-2"></i>
            <span>Balance pendiente: <span class="font-bold">{{ formatCurrency(remainingBalance) }}</span></span>
          </p>
        </div>

        <shopping-payment-box 
          :payment_methods="payment_methods"
          :balance="remainingBalance"
          :change="current_exchange_rate"
          @add-payment="addPayment"
        />
      </div>
    </div>

    <!-- Botón de anular compra -->
    <!-- <div 
      class="mt-6 p-6 bg-white rounded-lg shadow text-right space-x-4"
    >
      <button
        v-if="shopping.status !== 'paid'"
        @click="markAsPaid"
        class="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
      >
        Marcar como Pagada
      </button>
      <button
        v-if="shopping.status !== 'cancelled' && shopping.status !== 'paid'"
        @click="cancelShopping"
        class="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
      >
        Anular Compra
      </button>
    </div> -->
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import ShoppingPaymentBox from '@/Shared/ShoppingPaymentBox'
import { Inertia } from '@inertiajs/inertia'

export default {
  components: {
    Head,
    Link,
    ShoppingPaymentBox
  },

  layout: Layout,

  props: {
    shopping: {
      type: Object,
      required: true
    },
    total_paid: {
      type: Number,
      required: true
    },
    payment_methods: {
      type: Array,
      required: true
    },
    current_exchange_rate: {
      type: Number,
      required: true
    },
    user: {
      type: Object,
      required: true
    }
  },

  computed: {
    canManagePayments() {
      return ['financial', 'admin', 'sales'].includes(this.user.role) && 
             this.shopping.status !== 'paid' && 
             this.shopping.status !== 'cancelled'
    },

    remainingBalance() {
      return this.shopping.total - this.total_paid
    }
  },

  methods: {
    formatCurrency(value, currency = 'USD') {
      return new Intl.NumberFormat('es-US', {
        style: 'currency',
        currency: currency
      }).format(value || 0)
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },

    getPaymentMethodName(methodId) {
      const method = this.payment_methods.find(m => m.id === methodId)
      return method ? method.name : 'Desconocido'
    },

    addPayment(payment) {
      // this.$refs.paymentBox.resetForm();
      const payload = {
        shopping_id: this.shopping.id,
        method: payment.method,
        amount_usd: parseFloat(payment.amount) || 0,
        amount_ves: payment.amount_bs || 0,
        exchange_rate: this.current_exchange_rate,
        reference: payment.reference || ''
      };

      // Validar que el pago no exceda el saldo adeudado
      if (payload.amount_usd > this.remainingBalance) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Monto excedido',
          text: 'El monto del pago excede el saldo adeudado',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#3085d6'
        });
        return;
      }

      Inertia.post('/shoppings/pay', payload, {
        onSuccess: () => {
          // Resetear el componente de pago después de una operación exitosa
          this.$refs.paymentBox.resetForm();
          
          // Actualizar el total pagado y el estado
          this.shopping.total_paid += payload.amount_usd;
          if (this.shopping.total_paid >= this.shopping.total) {
            this.shopping.status = 'paid';
          }
          
          // Agregar el nuevo pago a la lista
          this.shopping.payments.push({
            created_at: new Date().toISOString(),
            payment_method_id: payload.method,
            amount_usd: payload.amount_usd,
            amount_ves: payload.amount_ves,
            exchange_rate: payload.exchange_rate,
            reference: payload.reference
          });
        },
        preserveScroll: true,
        preserveState: false
      });
    },

    back() {
      this.$inertia.visit('/shoppings')
    },

    cancelShopping() {
      Swal.fire({
        title: '¿Está seguro?',
        text: '¿Desea anular esta compra?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sí, anular',
        cancelButtonText: 'Cancelar'
      }).then((result) => {
        if (result.isConfirmed) {
          this.$inertia.get(`/shoppings/${this.shopping.id}/cancel`);
        }
      });
    },

    markAsPaid() {
      this.$inertia.get(`/shoppings/${this.shopping.id}/paid`)
    }
  }
}
</script>
